/**
 * 📥 COFFRE SPATIAL DES FLYERS - Téléchargement avec mystère !
 * Les flyers sont cachés/floutés, il faut les "déverrouiller" pour les recevoir
 * Sons de vaisseau spatial inclus
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Mail, X, Check, Lock, Unlock, Sparkles, Rocket, Volume2, VolumeX } from 'lucide-react';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const FLYERS = [
  {
    id: 'flyer_space',
    name: 'Flyer Galactique',
    description: 'Découvrez nos produits stars',
    image: 'https://customer-assets.emergentagent.com/job_starship-ui/artifacts/0l4bv279_flyerspace.png',
    color: 'cyan'
  },
  {
    id: 'flyer_delivery',
    name: 'Flyer Livraison',
    description: 'Livraison gratuite & offres',
    image: 'https://customer-assets.emergentagent.com/job_starship-ui/artifacts/qk7mzwia_Livraison%20gratuite%20night%2813%29.png',
    color: 'purple'
  }
];

// 🔊 Sons de vaisseau spatial
const createAudioContext = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new (window.AudioContext || window.webkitAudioContext)();
  } catch (e) {
    return null;
  }
};

const playVaultOpenSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;
    
    // Son de déverrouillage hydraulique
    const noise = ctx.createBufferSource();
    const bufferSize = ctx.sampleRate * 0.8;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(200, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.3);
    filter.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.8);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start();
    noise.stop(ctx.currentTime + 0.8);

    // Son de "clunk" métallique
    setTimeout(() => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.1);
      g.gain.setValueAtTime(0.4, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    }, 300);
  } catch (e) {}
};

const playHoverSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;
    
    // Petit bleep de vaisseau
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.05);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  } catch (e) {}
};

const playSuccessSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;
    
    // Mélodie de succès spatiale
    const playNote = (freq, delay) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.2);
      }, delay);
    };
    
    playNote(523, 0);    // Do
    playNote(659, 100);  // Mi
    playNote(784, 200);  // Sol
    playNote(1047, 300); // Do aigu
  } catch (e) {}
};

// Carte de Flyer (floutée avec cadenas)
const FlyerVaultCard = ({ flyer, isUnlocking, onUnlock, soundEnabled }) => {
  const colorMap = {
    cyan: { glow: 'rgba(6, 182, 212, 0.5)', border: 'border-cyan-500/50' },
    purple: { glow: 'rgba(139, 92, 246, 0.5)', border: 'border-purple-500/50' }
  };
  const colors = colorMap[flyer.color];

  return (
    <motion.div
      className={`relative rounded-2xl overflow-hidden border-2 ${colors.border}`}
      style={{ boxShadow: `0 0 30px ${colors.glow}` }}
      whileHover={{ scale: 1.02 }}
      onHoverStart={() => soundEnabled && playHoverSound()}
    >
      {/* Image floutée */}
      <div className="relative aspect-[3/4] overflow-hidden">
        <img 
          src={flyer.image} 
          alt={flyer.name}
          className="w-full h-full object-cover"
          style={{ filter: 'blur(20px) brightness(0.5)' }}
        />
        
        {/* Overlay avec cadenas */}
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
          <motion.div
            className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-cyan-500/50 flex items-center justify-center mb-4"
            animate={{
              boxShadow: ['0 0 20px rgba(6, 182, 212, 0.3)', '0 0 40px rgba(6, 182, 212, 0.6)', '0 0 20px rgba(6, 182, 212, 0.3)']
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Lock className="w-10 h-10 text-cyan-400" />
          </motion.div>
          
          <h4 className="text-white font-bold text-lg mb-1">{flyer.name}</h4>
          <p className="text-gray-400 text-sm mb-4">{flyer.description}</p>
          
          <motion.button
            onClick={() => onUnlock(flyer.id)}
            className="px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)',
              boxShadow: '0 0 20px rgba(6, 182, 212, 0.4)'
            }}
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(6, 182, 212, 0.6)' }}
            whileTap={{ scale: 0.95 }}
            data-testid={`unlock-${flyer.id}`}
          >
            <Unlock className="w-5 h-5" />
            Déverrouiller
          </motion.button>
        </div>

        {/* Scan effect */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(transparent 50%, rgba(6, 182, 212, 0.1) 50%)',
            backgroundSize: '100% 4px'
          }}
        />
      </div>
    </motion.div>
  );
};

// Modal de capture email
const UnlockModal = ({ isOpen, onClose, selectedFlyer, onSuccess, soundEnabled }) => {
  const [formData, setFormData] = useState({ prenom: '', email: '', codePostal: '', wantPromo: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.prenom || !formData.email) {
      toast.error('Prénom et email obligatoires');
      return;
    }

    setIsSubmitting(true);
    try {
      await fetch(`${API}/api/prospects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prenom: formData.prenom,
          email: formData.email,
          codePostal: formData.codePostal,
          telephone: '',
          flyerRequested: selectedFlyer,
          wantPromoCode: formData.wantPromo
        })
      });
      
      if (soundEnabled) playSuccessSound();
      setIsSuccess(true);
      toast.success('🚀 Vérifiez votre boîte mail !');
      setTimeout(() => onSuccess(), 2000);
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setIsSubmitting(false);
    }
  };

  const flyer = FLYERS.find(f => f.id === selectedFlyer);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(2, 6, 23, 0.95)', backdropFilter: 'blur(10px)' }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative w-full max-w-md rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(139, 92, 246, 0.1))',
              border: '2px solid rgba(6, 182, 212, 0.3)',
              boxShadow: '0 0 60px rgba(6, 182, 212, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20">
              <X className="w-5 h-5 text-white" />
            </button>

            {isSuccess ? (
              <div className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500 flex items-center justify-center"
                >
                  <Check className="w-10 h-10 text-white" />
                </motion.div>
                <h3 className="text-2xl font-bold text-white mb-2">Transmission Réussie !</h3>
                <p className="text-gray-300">Vérifiez votre boîte mail pour recevoir votre flyer en HD !</p>
              </div>
            ) : (
              <div className="p-6">
                <div className="text-center mb-6">
                  <motion.div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 mb-4"
                    animate={{ boxShadow: ['0 0 15px rgba(6, 182, 212, 0.3)', '0 0 30px rgba(6, 182, 212, 0.5)', '0 0 15px rgba(6, 182, 212, 0.3)'] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Download className="w-8 h-8 text-cyan-400" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white">Déverrouiller {flyer?.name}</h3>
                  <p className="text-gray-400 text-sm">Recevez-le en haute qualité par email !</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Votre prénom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500 outline-none"
                    required
                  />
                  <input
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500 outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Code postal (optionnel)"
                    value={formData.codePostal}
                    onChange={(e) => setFormData({ ...formData, codePostal: e.target.value })}
                    maxLength={5}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500 outline-none"
                  />
                  
                  <label className="flex items-center gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.wantPromo}
                      onChange={(e) => setFormData({ ...formData, wantPromo: e.target.checked })}
                      className="w-5 h-5 rounded text-yellow-500"
                    />
                    <div>
                      <p className="text-yellow-400 font-semibold text-sm">+ Recevoir mon code -10%</p>
                      <p className="text-gray-500 text-xs">Pour votre 1ère visite</p>
                    </div>
                  </label>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)' }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? (
                      <motion.div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }} />
                    ) : (
                      <><Rocket className="w-5 h-5" /> Recevoir mon flyer HD</>
                    )}
                  </motion.button>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Section principale
const FlyerVaultSection = () => {
  const [selectedFlyer, setSelectedFlyer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const handleUnlock = (flyerId) => {
    if (soundEnabled) playVaultOpenSound();
    setSelectedFlyer(flyerId);
    setShowModal(true);
  };

  return (
    <section className="relative py-20 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 mb-4"
        >
          <Lock className="w-4 h-4 text-cyan-400" />
          <span className="text-cyan-400 text-sm font-mono uppercase tracking-wider">Coffre Sécurisé</span>
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="ml-2 p-1 rounded-full hover:bg-white/10"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4 text-cyan-400" /> : <VolumeX className="w-4 h-4 text-gray-500" />}
          </button>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-black text-white mb-4"
        >
          Nos Flyers <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Secrets</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-gray-400 max-w-xl mx-auto"
        >
          Déverrouillez le coffre pour recevoir nos flyers en haute qualité directement par email !
        </motion.p>
      </div>

      {/* Flyers Grid */}
      <div className="max-w-3xl mx-auto">
        <div className="grid grid-cols-2 gap-6">
          {FLYERS.map((flyer) => (
            <FlyerVaultCard
              key={flyer.id}
              flyer={flyer}
              onUnlock={handleUnlock}
              soundEnabled={soundEnabled}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      <UnlockModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        selectedFlyer={selectedFlyer}
        onSuccess={() => setShowModal(false)}
        soundEnabled={soundEnabled}
      />
    </section>
  );
};

export default FlyerVaultSection;
